/**
 * Silently ensure offline catalog access when the embedded app loads.
 * Merchants never need a "sync catalog" button in production.
 *
 * Flow (Built for Shopify / App Store 1.1.1):
 * 1. Install stores offline token via OAuth callback, OR
 * 2. App Bridge session token → backend token exchange (automatic)
 *
 * Does not rely on localStorage for primary auth — only marks session linked.
 */

declare global {
  interface Window {
    shopify?: {
      idToken?: () => Promise<string>;
    };
  }
}

export async function ensureCatalogAccess(shop: string): Promise<boolean> {
  if (!shop || shop.startsWith('demo.')) return false;

  // Already linked this browser tab
  try {
    if (sessionStorage.getItem(`gf_catalog_ok:${shop}`) === '1') {
      return true;
    }
  } catch {
    /* ignore */
  }

  const sessionToken = await waitForSessionToken(4000);
  if (!sessionToken) {
    return false;
  }

  try {
    const res = await fetch('/api/v1/auth/session-exchange', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shop-Domain': shop,
      },
      body: JSON.stringify({ shop, sessionToken }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as {
      ok?: boolean;
      catalogLinked?: boolean;
      token?: string;
    };
    // Offline catalog link only. Production embedded API auth uses App Bridge
    // idToken (1.1.1) — store app JWT in sessionStorage for non-embedded/dev only.
    if (data.token && import.meta.env.DEV) {
      try {
        sessionStorage.setItem('gf_token', data.token);
      } catch {
        /* ignore */
      }
    }
    if (data.ok || data.catalogLinked) {
      try {
        sessionStorage.setItem(`gf_catalog_ok:${shop}`, '1');
      } catch {
        /* ignore */
      }
      return true;
    }
  } catch {
    /* ignore — catalog falls back until next load */
  }
  return false;
}

function waitForSessionToken(timeoutMs: number): Promise<string | null> {
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = async () => {
      try {
        if (window.shopify?.idToken) {
          const t = await window.shopify.idToken();
          if (t) {
            resolve(t);
            return;
          }
        }
      } catch {
        /* App Bridge not ready */
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(null);
        return;
      }
      window.setTimeout(() => void tick(), 200);
    };
    void tick();
  });
}
